import {
  Button,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type ModelProviderWhitelistResponseDto } from '@inference.engine/api-client';
import { PaginationControls } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SortableHead, type SortDirection } from '@/shared/ui/data-table';
import { TABLE_COL_WIDTH } from '@/shared/constants';
import { ADMIN_TABLE_STYLES } from '../adminTableStyles';

export interface WorkspaceRule {
  workspaceId: string;
  workspaceName: string;
  providers: ModelProviderWhitelistResponseDto[];
}

type SortableWorkspaceRuleKeys = 'workspaceName';

const WORKSPACE_COLUMN: SortableWorkspaceRuleKeys = 'workspaceName';

interface WorkspaceWhitelistRulesPagination {
  /** Total rules across all pages. */
  count: number;
  /** Current page, 0-based. */
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
}

interface WorkspaceWhitelistRulesTableProps {
  rules: WorkspaceRule[];
  pagination: WorkspaceWhitelistRulesPagination;
  sortDirection: SortDirection;
  onSort: (property: SortableWorkspaceRuleKeys) => void;
  onDelete: (workspaceId: string, workspaceName: string) => void;
}

export const WorkspaceWhitelistRulesTable = ({
  rules,
  pagination,
  sortDirection,
  onSort,
  onDelete,
}: WorkspaceWhitelistRulesTableProps) => {
  const { t } = useTranslation('admin');

  if (pagination.count === 0) {
    return (
      <EmptyState
        title={t('admin.workspaceWhitelistRulesTable.noWhitelistRuleAddedYet')}
        description={t('admin.workspaceWhitelistRulesTable.clickOnAddRuleTo')}
        height="400px"
      />
    );
  }

  return (
    <TooltipProvider>
      <div className="flex w-full flex-col">
        <Datatable
          aria-label={t('admin.workspaceWhitelistRulesTable.workspaceRules')}
          className={ADMIN_TABLE_STYLES}
        >
          <DatatableHeader>
            <DatatableRow>
              <SortableHead
                column={WORKSPACE_COLUMN}
                label={t('admin.workspaceWhitelistRulesTable.workspace')}
                className="w-[40%]"
                sortBy={WORKSPACE_COLUMN}
                sortDirection={sortDirection}
                onSort={onSort}
              />
              <DatatableHead scope="col">
                {t('admin.workspaceWhitelistRulesTable.whitelistedProviders')}
              </DatatableHead>
              <DatatableHead scope="col" className={TABLE_COL_WIDTH.actions} />
            </DatatableRow>
          </DatatableHeader>

          <DatatableBody>
            {rules.map((row) => {
              const deleteLabel = t('admin.workspaceWhitelistRulesTable.deleteRuleFor', {
                workspaceName: row.workspaceName,
              });
              return (
                <DatatableRow key={row.workspaceId}>
                  <DatatableCell className="text-body-md-strong font-medium">
                    {row.workspaceName}
                  </DatatableCell>
                  <DatatableCell className="text-body-md">
                    {row.providers.map((p) => p.name).join(' • ')}
                  </DatatableCell>
                  <DatatableCell className={TABLE_COL_WIDTH.actions}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={deleteLabel}
                          onClick={() => onDelete(row.workspaceId, row.workspaceName)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2Icon className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{deleteLabel}</TooltipContent>
                    </Tooltip>
                  </DatatableCell>
                </DatatableRow>
              );
            })}
          </DatatableBody>
        </Datatable>

        <PaginationControls
          page={pagination.page + 1}
          totalItems={pagination.count}
          pageSize={pagination.rowsPerPage}
          onPageChange={(nextPage) => pagination.onPageChange(nextPage - 1)}
          className="pt-section"
        />
      </div>
    </TooltipProvider>
  );
};

WorkspaceWhitelistRulesTable.displayName = 'WorkspaceWhitelistRulesTable';
